import { useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Dropdown, IconButton, Text } from '../Ant';
import {
  ArrowRight,
  Briefcase,
  Close,
  DocumentText,
  DocumentTextOutline,
  FilledLinearGraph,
  FilledNote,
  HamburgerMenu,
  LinearBriefcase,
  LinearChart,
  LinearNoteAdd,
  LinearReceipt,
  NoteAdd,
  Notes,
  ProfileUser,
  ProfileUserOutline,
  Receipt,
} from '../icons';
import UserAvatarCircle from '../userAvatarCircle/userAvatarCircle';
import { ROUTES, UserType } from '../../constants/constants';
import { FeatureSelectionTab } from '../../interface/featureSelection';
import { AnalyticsEvent, FeatureButtons } from '../../interface/analytics';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useFeatureContext } from '../../context/FeatureContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useUser } from '../../context/UserContext';

type NavigationIcon = React.ComponentType<Readonly<{ className?: string }>>;

interface NavigationDestination {
  feature: FeatureSelectionTab;
  route: string;
  labelKey: string;
  testId: string;
  analyticsButton: FeatureButtons;
  outlineIcon: NavigationIcon;
  filledIcon: NavigationIcon;
  hiddenForRestrictedUsers?: boolean;
}

const NAVIGATION_DESTINATIONS: NavigationDestination[] = [
  {
    feature: FeatureSelectionTab.dashboard,
    route: ROUTES.DASHBOARD,
    labelKey: 'common:bottomNavigation.dashboard',
    testId: 'dashboard-button',
    analyticsButton: FeatureButtons.DASHBOARD,
    outlineIcon: LinearChart,
    filledIcon: FilledLinearGraph,
  },
  {
    feature: FeatureSelectionTab.tasks,
    route: ROUTES.TASKS_LIST,
    labelKey: 'task:tasks',
    testId: 'tasks-button',
    analyticsButton: FeatureButtons.TASKS,
    outlineIcon: Notes,
    filledIcon: FilledNote,
  },
  {
    feature: FeatureSelectionTab.transactions,
    route: ROUTES.TRANSACTION_LISTING,
    labelKey: 'common:bottomNavigation.transactions',
    testId: 'transactions-button',
    analyticsButton: FeatureButtons.TRANSACTIONS,
    outlineIcon: DocumentTextOutline,
    filledIcon: DocumentText,
  },
  {
    feature: FeatureSelectionTab.customers,
    route: ROUTES.CUSTOMER_LISTING,
    labelKey: 'common:bottomNavigation.customers',
    testId: 'customers-button',
    analyticsButton: FeatureButtons.CUSTOMERS,
    outlineIcon: ProfileUserOutline,
    filledIcon: ProfileUser,
  },
  {
    feature: FeatureSelectionTab.matters,
    route: ROUTES.MATTERS_LISTING,
    labelKey: 'common:bottomNavigation.matters',
    testId: 'matters-button',
    analyticsButton: FeatureButtons.MATTERS,
    outlineIcon: LinearBriefcase,
    filledIcon: Briefcase,
  },
  {
    feature: FeatureSelectionTab.invoicing,
    route: ROUTES.INVOICING,
    labelKey: 'common:bottomNavigation.invoicing',
    testId: 'invoicing-button',
    analyticsButton: FeatureButtons.MY_INVOICING,
    outlineIcon: LinearReceipt,
    filledIcon: Receipt,
    hiddenForRestrictedUsers: true,
  },
  {
    feature: FeatureSelectionTab.workTimes,
    route: ROUTES.WORK_TIME,
    labelKey: 'common:bottomNavigation.worktime',
    testId: 'worktime-button',
    analyticsButton: FeatureButtons.WORKTIME,
    outlineIcon: LinearNoteAdd,
    filledIcon: NoteAdd,
  },
];

const NavigationMenuItemLabel = ({
  testId,
  icon,
  label,
  isActive,
}: Readonly<{
  testId: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}>) => (
  <>
    <Flex
      gap={10}
      align="center"
      data-testid={testId}
      className={`menu-item-btn ${isActive ? 'active-item' : ''}`}
    >
      {icon}
      <Text $fontsize="13px" $color="description" $weight={500} $lineheight="17px">
        {label}
      </Text>
    </Flex>
    <ArrowRight />
  </>
);

const AppNavigationMenu = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userDetails } = useUser();
  const { features } = useFeatureContext();
  const { clear, push } = useNavigationStack();
  const { trackAction } = useAnalytics();
  const [menuVisible, setMenuVisible] = useState(false);

  const isRestrictedUser = userDetails?.userType === UserType.RESTRICTED;

  const destinationItems = NAVIGATION_DESTINATIONS.filter(
    (destination) => !(destination.hiddenForRestrictedUsers && isRestrictedUser),
  ).map((destination) => {
    const isActive = location.pathname === destination.route;
    const Icon = isActive ? destination.filledIcon : destination.outlineIcon;

    return {
      key: String(destination.feature),
      label: (
        <NavigationMenuItemLabel
          testId={destination.testId}
          icon={<Icon />}
          label={t(destination.labelKey)}
          isActive={isActive}
        />
      ),
      onClick: () => {
        trackAction(AnalyticsEvent.BUTTON_CLICK, destination.analyticsButton);
        clear();
        push(destination.route);
      },
    };
  });

  const profileItem = {
    key: String(FeatureSelectionTab.profile),
    label: (
      <NavigationMenuItemLabel
        testId="profile-button"
        icon={
          <UserAvatarCircle imageUrl={userDetails?.imageUrl} fullName={userDetails?.fullName} />
        }
        label={t('common:bottomNavigation.profile')}
        isActive={location.pathname === ROUTES.PROFILE}
      />
    ),
    // Profile keeps the navigation stack so the user returns to the page they opened it from.
    onClick: () => {
      trackAction(AnalyticsEvent.BUTTON_CLICK, FeatureButtons.PROFILE);
      push(ROUTES.PROFILE);
    },
  };

  const menuItems = [...destinationItems, profileItem].filter((item) => !!features[item.key]);

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: (e) => {
          const clickedItem = menuItems.find((item) => item.key === e.key);
          clickedItem?.onClick?.();
          setMenuVisible(false);
        },
      }}
      open={menuVisible}
      onOpenChange={setMenuVisible}
      trigger={['click']}
      overlayClassName="dashboard-menu"
      data-testid="menu-dropdown"
    >
      <Tooltip title={t('common:menu')} placement="top" arrow={false}>
        <IconButton
          icon={
            menuVisible ? <Close className="close-btn-icon" /> : <HamburgerMenu className="fz-22" />
          }
          className={`dashboard-btn ${menuVisible ? 'page-active' : ''}`}
          onClick={(e) => e.preventDefault()}
          data-testid="menu-button"
          aria-label={t('common:menu')}
          size="large"
        />
      </Tooltip>
    </Dropdown>
  );
};

export default AppNavigationMenu;
